import { ReactNode } from 'react';
import {
  BellRing,
  CalendarCheck,
  CalendarDays,
  BarChart3,
  Check,
  CircleCheck,
  Globe,
  LayoutTemplate,
  Mail,
  Megaphone,
  Minus,
  MousePointerClick,
  Palette,
  PenTool,
  ShieldCheck,
  Smartphone,
  SlidersHorizontal,
  Sparkles,
  Star,
  Users,
  X,
} from 'lucide-react';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { ProductShot, ProductToast } from '@/components/marketing/ProductShot';
import { CtaLink, Eyebrow, PageHero, Reveal, SectionHeading } from '@/components/marketing/primitives';
import { cn } from '@/lib/utils';
import tenantWebsiteShot from '@/assets/product/tenant-website.webp';
import tenantBookingShot from '@/assets/product/tenant-booking.webp';
import scheduleTeamShot from '@/assets/product/schedule-team-main.webp';
import paymentsShot from '@/assets/product/payments-main.webp';
import '@/styles/marketing-pages.css';

/**
 * Sales features page. Four outcome-led chapters (win, book, run, control),
 * a grid of the smaller time-savers, then the "why not stitch tools" table.
 */
const MarketingFeatures = () => (
  <MarketingLayout>
    <PageHero
      eyebrow={
        <>
          <MousePointerClick className="h-3.5 w-3.5 text-[hsl(var(--mk-sun))]" /> Features
        </>
      }
      title={
        <>
          Everything it takes to <span className="mk-mark mk-mark-block text-[hsl(var(--mk-ink))]">fill your diary.</span>
        </>
      }
      lede="Get found on Google and in AI search, take bookings while you sleep, send the right cleaner to the right door and get paid on the day. Here’s how."
    >
      <div className="flex flex-col justify-center gap-3 sm:flex-row">
        <CtaLink to="/signup">Start your free trial</CtaLink>
        <CtaLink to="/demo" variant="ghost">
          Look round a demo site
        </CtaLink>
      </div>
      <nav aria-label="Feature sections" className="mt-14 flex flex-wrap justify-center gap-2">
        {chapters.map((c) => (
          <a
            key={c.id}
            href={`#${c.id}`}
            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
          >
            <c.icon className="h-4 w-4 text-[hsl(var(--mk-mint))]" /> {c.nav}
          </a>
        ))}
        <a
          href="#ai-tools"
          className="flex items-center gap-2 rounded-full border border-[hsl(var(--mk-sun)/0.5)] bg-white/[0.06] px-4 py-2 text-sm text-white transition-colors hover:border-[hsl(var(--mk-sun))]"
        >
          <Sparkles className="h-4 w-4 text-[hsl(var(--mk-sun))]" /> AI tools
        </a>
      </nav>
    </PageHero>

    <section className="py-24 md:py-32">
      <div className="container mx-auto space-y-28 px-4 md:space-y-36">
        {chapters.map((c, i) => (
          <Chapter key={c.id} chapter={c} index={i} />
        ))}
      </div>
    </section>

    <AiTools />
    <TimeSavers />
    <Comparison />
  </MarketingLayout>
);

/* -------------------------------- Chapters -------------------------------- */

type ChapterData = {
  id: string;
  nav: string;
  icon: typeof Globe;
  tint: string;
  panel: string;
  eyebrow: string;
  title: ReactNode;
  body: string;
  points: string[];
  visual: ReactNode;
};

const chapters: ChapterData[] = [
  {
    id: 'website',
    nav: 'Your website',
    icon: Megaphone,
    tint: 'bg-[hsl(var(--mk-sun))]',
    panel: 'bg-[hsl(var(--mk-lilac))]',
    eyebrow: 'Get found',
    title: (
      <>
        Look like the <span className="mk-mark">obvious choice.</span>
      </>
    ),
    body: 'Get discovered. Make a great first impression. Win more customers. Your professional website is designed to help local customers find you through Google, Bing and AI search tools like ChatGPT. Fast, mobile-friendly and built to turn visitors into bookings, with your services and prices clearly displayed. And when you need to update something? Change your prices, photos or content in just a few clicks, without paying a web designer.',
    points: [
      'Search-ready from day one: page titles, descriptions, a sitemap and the areas you cover',
      'Your services, prices, areas and hours spelled out for Google and AI assistants like ChatGPT',
      'Themes made for cleaning companies, in your logo and colours',
      'No logo yet? Design one with AI on Team and Business',
      'Change any page, photo or price yourself',
      'Your Google star rating and latest reviews, pulled onto your site live',
      'Connect a domain you already own on Team and Business',
    ],
    visual: <WebsiteMock />,
  },
  {
    id: 'bookings',
    nav: 'Bookings & quotes',
    icon: CalendarCheck,
    tint: 'bg-[hsl(var(--mk-mint))]',
    panel: 'bg-[hsl(var(--mk-mint)/0.22)]',
    eyebrow: 'Get booked',
    title: (
      <>
        Bookings that land <span className="mk-mark mk-mark-mint">while you sleep.</span>
      </>
    ),
    body: 'The first cleaner to reply often gets the job. What if you didn’t need to reply at all? Customers can check your availability, choose a service and book online, 24/7. Offer instant bookings at fixed prices or let customers request and accept personalised quotes. You control whether bookings are confirmed automatically or need your approval. Less waiting for customers, fewer missed opportunities for you.',
    points: [
      'Live free slots, so nobody books a time you’re busy',
      'Confirm bookings instantly, or approve each one yourself. Your call.',
      'Each service is fixed price (book straight away) or quote only (request, you price it, they accept)',
      'Regulars booked weekly, fortnightly, 4-weekly or monthly',
      'Reminder emails, so customers remember you’re coming (and text message reminders on Team and Business, through your own Twilio account)',
      'Every enquiry in one inbox, so none slip through the cracks',
      'On Business, type a few notes about a job and AI drafts the quote for you to check',
    ],
    visual: <BookingMock />,
  },
  {
    id: 'team',
    nav: 'Schedule & team',
    icon: Users,
    tint: 'bg-[hsl(var(--mk-lavender))]',
    panel: 'bg-[hsl(var(--mk-panel))]',
    eyebrow: 'Get organised',
    title: (
      <>
        Everyone knows <span className="mk-mark">where to be.</span>
      </>
    ),
    body: 'No more Sunday-night texts working out who’s going where. Every job sits on one schedule. Put a cleaner on it and it shows up in their own login, with their jobs and nobody else’s.',
    points: [
      'The whole team’s week on one screen',
      'Move a job to someone else when a cleaner’s off sick',
      'Supervisors who can run the diary for you',
      'Cleaners only ever see their own jobs',
      'Jobs sync to the calendar on their phone',
    ],
    visual: <TeamMock />,
  },
  {
    id: 'admin',
    nav: 'Payments & admin',
    icon: SlidersHorizontal,
    tint: 'bg-[hsl(var(--mk-sun))]',
    panel: 'bg-[hsl(var(--mk-lilac))]',
    eyebrow: 'Get paid',
    title: (
      <>
        Get paid on the day, <span className="mk-mark mk-mark-mint">not next month.</span>
      </>
    ),
    body: 'Card customers pay when they book, in full or a deposit, so a no-show doesn’t cost you. Happy to take cash on the day? Offer that too, it’s your call. Invoices for anything else go out with a link to pay online, and if you ever leave, your data comes with you.',
    points: [
      'Card payments through Stripe, PayPal or good old cash',
      'We take nothing from your jobs (Stripe’s usual card fee applies)',
      'Choose full payment or a deposit when customers book by card',
      'Download your bookings and customers whenever you like',
    ],
    visual: <AdminMock />,
  },
];

function Chapter({ chapter: c, index }: { chapter: ChapterData; index: number }) {
  const flip = index % 2 === 1;
  return (
    <div id={c.id} className="grid scroll-mt-28 items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <div className={cn('min-w-0', flip && 'lg:order-2')}>
        <Reveal className="flex items-center gap-4">
          <span className={cn('mk-icon-blob text-[hsl(var(--mk-ink))]', c.tint)}>
            <c.icon className="h-6 w-6" />
          </span>
          <span className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--mk-muted))]">
            0{index + 1} · {c.eyebrow}
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="mk-h2 mt-7">{c.title}</h2>
          <p className="mk-lede mt-6 text-[hsl(var(--mk-muted))]">{c.body}</p>
        </Reveal>
        <ul className="mt-9 space-y-3.5">
          {c.points.map((p, i) => (
            <Reveal as="li" key={p} delay={140 + i * 60} className="flex items-start gap-3 text-lg">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-ink))] text-white">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
              {p}
            </Reveal>
          ))}
        </ul>
      </div>
      <Reveal delay={120} className={cn('relative min-w-0 rounded-[2.5rem] p-5 sm:p-10 md:p-12', c.panel, flip && 'lg:order-1')}>
        {c.visual}
      </Reveal>
    </div>
  );
}

/* ---------------------------------- Mocks --------------------------------- */


function FloatChip({ className, icon, tint, title, body }: { className?: string; icon: ReactNode; tint: string; title: string; body: string }) {
  return (
    <div
      className={cn(
        'mk-float absolute z-10 hidden items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_20px_40px_-16px_rgba(10,8,40,0.4)] sm:flex',
        className
      )}
      aria-hidden
    >
      <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl', tint)}>{icon}</span>
      <span className="leading-tight">
        <span className="block font-display text-sm font-semibold">{title}</span>
        <span className="block text-xs text-[hsl(var(--mk-muted))]">{body}</span>
      </span>
    </div>
  );
}

function WebsiteMock() {
  return (
    <div className="relative">
      <ProductShot
        src={tenantWebsiteShot}
        alt="Maid Madness's website, built on The Polished Version, with a Book now button and star rating up top"
        url="maidmadness.thepolishedversion.com"
        width={1600}
        height={1000}
        testId="marketing-website-shot"
      />
      <ProductToast
        className="-top-7 right-[6%]"
        delay={0}
        icon={<CalendarCheck className="h-4 w-4" />}
        tint="bg-[hsl(var(--mk-sun))]"
        title="New booking"
        body="Deep clean · Sat 10:00"
      />
      <ProductToast
        className="-left-3 top-[48%] sm:-left-10"
        delay={2300}
        icon={<BellRing className="h-4 w-4" />}
        tint="bg-[hsl(var(--mk-mint))]"
        title="Team notified"
        body="Aisha's on it"
      />
      <ProductToast
        className="-bottom-6 right-[8%]"
        delay={4600}
        icon={<Check className="h-4 w-4" />}
        tint="bg-[hsl(var(--mk-lavender))]"
        title="Quote accepted"
        body="End of tenancy · Mon"
      />
    </div>
  );
}

function BookingMock() {
  return (
    <div className="relative">
      <ProductShot
        src={tenantBookingShot}
        alt="The online booking form: services with prices, then a date and time"
        url="maidmadness.thepolishedversion.com/booking"
        width={1200}
        height={1065}
        testId="marketing-booking-shot"
      />
      <FloatChip
        className="-right-4 -top-8 sm:-right-8"
        icon={<BellRing className="h-4 w-4" />}
        tint="bg-[hsl(var(--mk-mint))]"
        title="Booking confirmed"
        body="Regular clean · Sat 10:30"
      />
    </div>
  );
}

const roles = [
  { role: 'Owner', sees: 'Everything', tint: 'bg-[hsl(var(--mk-sun))]' },
  { role: 'Supervisor', sees: 'All calendars + enquiries', tint: 'bg-[hsl(var(--mk-mint))]' },
  { role: 'Cleaner', sees: 'Their own jobs only', tint: 'bg-[hsl(var(--mk-lavender))]' },
];

function TeamMock() {
  return (
    <div className="relative space-y-4">
      <ProductShot
        src={scheduleTeamShot}
        alt="The team schedule: every cleaner in their own column for the day"
        width={1400}
        height={916}
        testId="marketing-schedule-shot"
      />
      <div className="grid gap-2.5 sm:grid-cols-2" aria-hidden>
        {roles.map((r) => (
          <div key={r.role} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_12px_30px_-20px_rgba(20,16,70,0.5)]">
            <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', r.tint)}>
              <ShieldCheck className="h-4 w-4" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block font-display text-sm font-semibold">{r.role}</span>
              <span className="block text-xs text-[hsl(var(--mk-muted))]">{r.sees}</span>
            </span>
          </div>
        ))}
        <div className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--mk-ink))] p-3 text-white">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10">
            <CalendarDays className="h-4 w-4 text-[hsl(var(--mk-mint))]" />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-sm font-semibold">iCal sync on</span>
            <span className="block text-xs text-white/60">Jobs show on their phone</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function AdminMock() {
  return (
    <div className="relative">
      <ProductShot
        src={paymentsShot}
        alt="Payment settings: Stripe card payments connected, PayPal.me active, and cash in person"
        width={1400}
        height={916}
        testId="marketing-payments-shot"
      />
      <FloatChip
        className="-bottom-8 -left-4 sm:-left-8"
        icon={<Check className="h-4 w-4" />}
        tint="bg-[hsl(var(--mk-sun))]"
        title="£0 commission"
        body="Every booking is yours"
      />
    </div>
  );
}

/* -------------------------------- AI tools -------------------------------- */

// Mirrored word for word in the crawler copy (src/lib/marketingSeo.ts).
const AI_TOOLS_LEDE =
  'Two AI helpers are built in, for the jobs that usually eat an evening. You stay in charge: you pick the logo, and you check every quote before it goes.';

const aiTools = [
  {
    icon: PenTool,
    title: 'AI logo maker',
    plan: 'On Team and Business',
    body: 'No logo yet? Type in your business name, pick a style and your colours, and get three designs to choose from in seconds. Pick the one you like and it’s your logo, on a clean see-through background, ready for your website. Not quite right? Have another go.',
    points: ['Three designs every go', 'Your colours, your style', 'On your website in one click'],
  },
  {
    icon: Sparkles,
    title: 'AI proposal writer',
    plan: 'On Business',
    body: 'A customer asks for a quote. Instead of starting from a blank page, jot down the job in your own words and AI drafts the quote for you, priced from your own services. Check it, change anything you like, then send it.',
    points: ['Priced from your own services', 'Your words, tidied into a proper quote', 'Nothing goes out until you’ve checked it'],
  },
];

const AiTools = () => (
  <section id="ai-tools" className="scroll-mt-28 px-4 pb-24 md:pb-32">
    <div className="container mx-auto">
      <SectionHeading
        eyebrow="AI tools"
        title={
          <>
            A logo and a quote, <span className="mk-mark">done in seconds.</span>
          </>
        }
        lede={AI_TOOLS_LEDE}
      />
      <div className="mx-auto mt-14 grid max-w-5xl gap-5 md:grid-cols-2">
        {aiTools.map((t, i) => (
          <Reveal key={t.title} as="article" delay={i * 90} className="mk-card flex flex-col p-8 md:p-10">
            <div className="flex items-start justify-between gap-4">
              <span className="mk-icon-blob bg-[hsl(var(--mk-sun))] text-[hsl(var(--mk-ink))]">
                <t.icon className="h-6 w-6" strokeWidth={2} />
              </span>
              <span className="rounded-full bg-[hsl(var(--mk-panel))] px-3 py-1 text-xs font-semibold text-[hsl(var(--mk-ink))]">
                {t.plan}
              </span>
            </div>
            <h3 className="mt-7 text-2xl">{t.title}</h3>
            <p className="mt-3 text-[hsl(var(--mk-muted))]">{t.body}</p>
            <ul className="mt-6 space-y-2.5">
              {t.points.map((pt) => (
                <li key={pt} className="flex items-start gap-2.5">
                  <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--mk-mint-deep))]" /> {pt}
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------- Time savers ------------------------------ */

const savers = [
  { icon: Globe, title: 'Connect your domain', body: 'Already own a domain? Connect it on Team and Business and we set up the secure connection for you.' },
  { icon: Palette, title: 'Your brand, everywhere', body: 'Your logo and colours, from the homepage right through to booking.' },
  { icon: LayoutTemplate, title: 'Extra pages', body: 'Add a page for each town you cover, or a careers page, in minutes.' },
  { icon: Star, title: 'Google reviews', body: 'Connect your Google listing once and your star rating and latest reviews show on your site, always up to date.' },
  { icon: CalendarDays, title: 'Calendar sync', body: 'Jobs show up in Google, Apple or Outlook calendar.' },
  { icon: Smartphone, title: 'Made for phones', body: 'Most people book from the sofa on their phone. It’s built for that.' },
  { icon: Mail, title: 'Email and text reminders', body: 'Customers get a reminder before you’re due, so fewer wasted trips. Text reminders are on Team and Business: connect your own Twilio account and Twilio bills you for the texts.' },
  { icon: BarChart3, title: 'Insights', body: 'Which cleans earn the most, and who still owes you.' },
];

const TimeSavers = () => (
  <section className="px-4">
    <div className="mk-ink-bg mk-on-dark container mx-auto overflow-hidden rounded-[2.5rem] px-6 py-20 md:px-14 md:py-24">
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
        <Reveal>
          <Eyebrow>The little things</Eyebrow>
          <h2 className="mk-h2 mt-6 text-white">
            Small details. <span className="text-[hsl(var(--mk-mint))]">Big hours back.</span>
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <p className="mk-lede text-white/65 lg:max-w-lg lg:justify-self-end">
            The bits that usually mean another app, another login or another favour from a mate who “does websites”. They’re all in here.
          </p>
        </Reveal>
      </div>
      <div className="mt-12 grid grid-cols-2 gap-3 md:mt-14 md:gap-4 lg:grid-cols-4">
        {savers.map((s, i) => (
          <Reveal
            key={s.title}
            delay={(i % 4) * 70}
            className="rounded-[1.75rem] border border-white/10 bg-white/[0.05] p-5 transition-colors sm:p-6 hover:border-white/25 hover:bg-white/[0.08]"
          >
            <s.icon className="h-6 w-6 text-[hsl(var(--mk-sun))]" />
            <h3 className="mt-4 text-base text-white sm:mt-5 sm:text-lg">{s.title}</h3>
            <p className="mt-2 text-sm text-white/60">{s.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------- Comparison ------------------------------ */

type ComparisonValue = 'yes' | 'partial' | 'no';

const comparisonRows: [string, ComparisonValue, ComparisonValue, ComparisonValue, ComparisonValue][] = [
  ['A proper website, included', 'yes', 'yes', 'no', 'yes'],
  ['Designed for cleaning companies', 'yes', 'no', 'no', 'partial'],
  ['Customers book and ask for quotes on your site', 'yes', 'partial', 'yes', 'yes'],
  ['Change your website yourself', 'yes', 'yes', 'no', 'yes'],
  ['Team schedule, with who’s on which job', 'yes', 'no', 'partial', 'yes'],
  ['Simple enough for your cleaners', 'yes', 'partial', 'yes', 'partial'],
  ['Website, bookings and admin in one login', 'yes', 'no', 'no', 'yes'],
];

const columns = [
  { name: 'The Polished Version', price: 'From £9/mo' },
  { name: 'Website builders', price: 'Often £12 to £25/mo plus booking tools' },
  { name: 'Booking tools', price: 'Often £10 to £30/mo plus a website' },
  { name: 'Jobber', price: 'Typically higher monthly plans' },
];

// Fixed minimum widths on phones (the table scrolls); proportional from tablet up.
const COMPARISON_GRID =
  'grid-cols-[136px_repeat(4,112px)] md:grid-cols-[1.5fr_repeat(4,1fr)]';

const Comparison = () => (
  <section className="py-28 md:py-36">
    <div className="container mx-auto px-4">
      <SectionHeading
        eyebrow="Why not stitch tools together?"
        title={
          <>
            One login beats <span className="mk-mark">four subscriptions.</span>
          </>
        }
        lede="A website builder here, a booking app there, and a group chat holding it all together. It works, right up until a booking falls through the gap. Here’s how we compare."
      />

      <Reveal className="mt-16">
        {/* One table on every screen. Phones scroll it sideways, with the feature names pinned on the left. */}
        <p className="mb-3 text-center text-xs text-[hsl(var(--mk-muted))] md:hidden">Swipe the table sideways to see every option →</p>
        <div className="-mx-4 overflow-x-auto px-4 pb-2 md:mx-0 md:overflow-visible md:px-0 md:pb-0" data-testid="comparison-scroll">
          <div className="mk-panel w-max min-w-full p-2 md:w-auto md:p-4">
            <div className={cn('grid gap-2', COMPARISON_GRID)}>
              <div className="sticky left-0 z-10 flex items-end bg-[hsl(var(--mk-panel))] p-3 text-sm font-semibold text-[hsl(var(--mk-muted))] md:static md:bg-transparent md:p-4">
                What you get
              </div>
              {columns.map((col, idx) => (
                <div
                  key={col.name}
                  className={cn(
                    'rounded-t-3xl p-3 text-center md:p-4',
                    idx === 0 && 'mk-ink-bg text-white'
                  )}
                >
                  <div className="font-display text-sm font-semibold leading-tight md:text-base">{col.name}</div>
                  <div className={cn('mt-1.5 text-xs', idx === 0 ? 'font-semibold text-[hsl(var(--mk-sun))]' : 'text-[hsl(var(--mk-muted))]')}>
                    {col.price}
                  </div>
                </div>
              ))}
            </div>
            {/* overflow-clip (not hidden) rounds the corners without breaking the pinned column. */}
            <div className="overflow-clip rounded-3xl bg-white">
              {comparisonRows.map(([label, ...values], rowIdx) => (
                <div
                  key={label}
                  className={cn('grid gap-2', COMPARISON_GRID, rowIdx > 0 && 'border-t border-[hsl(var(--border))]')}
                >
                  <div className="sticky left-0 z-10 bg-white p-3 text-sm font-medium shadow-[6px_0_8px_-8px_rgba(0,0,0,0.25)] md:static md:p-4 md:text-base md:shadow-none">
                    {label}
                  </div>
                  {values.map((value, idx) => (
                    <div
                      key={`${label}-${idx}`}
                      className={cn('flex items-center justify-center p-3 md:p-4', idx === 0 && 'bg-[hsl(var(--mk-lilac))]')}
                    >
                      <ComparisonIcon value={value} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="mx-auto mt-5 max-w-2xl text-center text-xs text-[hsl(var(--mk-muted))]">
          Partial means it’s there, but as an extra, a workaround, or not really what that tool was built for.
        </p>
      </Reveal>

      <Reveal className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <CtaLink to="/pricing" variant="ink">
          See the pricing
        </CtaLink>
        <span className="flex items-center gap-2 text-sm text-[hsl(var(--mk-muted))]">
          <CircleCheck className="h-4 w-4 text-[hsl(var(--mk-mint-deep))]" /> Free for 30 days, then from £9 a month
        </span>
      </Reveal>
    </div>
  </section>
);

const ComparisonIcon = ({ value }: { value: ComparisonValue }) => {
  if (value === 'yes')
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-mint))]" aria-label="Yes">
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    );
  if (value === 'partial')
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-sun)/0.45)]" aria-label="Partial">
        <Minus className="h-4 w-4" strokeWidth={3} />
      </span>
    );
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-ink)/0.06)] text-[hsl(var(--mk-ink)/0.35)]" aria-label="No">
      <X className="h-4 w-4" />
    </span>
  );
};

export default MarketingFeatures;
